import { Checkbox, Dropdown, Field, NumericInput, RadioGroup, TextOutput } from 'Components/Core';
import { withInjectedProps } from 'Components/Core/InjectedPropsContext';
import { ListFieldSection } from 'Components/Layout';
import { WorksheetAssociatedTestSection } from 'Components/Worksheets/Core';
import { AssociatedTestType } from 'Components/Worksheets/Core/WorksheetAssociatedTestSection/types';
import { IWorksheetComponent } from 'Components/Worksheets/Core/types';
import ExternalTestMethods from 'Components/Worksheets/ExternalTestMethods';
import { getLabelForPortionState } from 'Components/Worksheets/PsdSieveAnalysisWorksheet/Helpers';
import { MaterialDryMassObtainedBy, StateOfMaterial } from 'Components/Worksheets/PsdSieveAnalysisWorksheet/types';
import { ISieveSize, SieveSizeId } from 'Data/ViewModels/Equipment/ISieveSize';
import { Guid } from 'Lib/Guid';
import { pipe } from 'Lib/Hooks/Selectors';
import deepEquals from 'Lib/Object/deepEquals';
import React from 'react';
import { useSelector } from 'react-redux';
import { AggregateType, AsNominalParticleSize } from '../../types';
import AggsoilSpecimenPreparationPageSection from './AggsoilSpecimenPreparationSection';

let AsphaltSpecimenPreparationSection: React.FC<IWorksheetComponent> = ({ createWorksheetSelector }) => {
  const materialDryMassObtainedBy: MaterialDryMassObtainedBy = useSelector(
    createWorksheetSelector!('Preparation.MaterialDryMassObtainedBy')
  );

  const measuredDryMass = materialDryMassObtainedBy === MaterialDryMassObtainedBy.Measured;
  const importedDryMass = materialDryMassObtainedBy === MaterialDryMassObtainedBy.Imported;
  const importedBinderContentTestId: Guid = useSelector(
    createWorksheetSelector!('WholePortion.Masses.AssociatedBinderOrBitumenContentTest.ImportedTest.Id')
  );

  const supportedNominalParticleSize: SieveSizeId[] = ['75_0', '40_0', '28_0', '20_0', '14_0', '10_0', '7_0', '5_0'];

  const availableSieveSizes = useSelector(
    pipe(createWorksheetSelector!('data.Equipment.AllSieveSizes', false), (sieveSize) =>
      sieveSize.filter((s: ISieveSize) => supportedNominalParticleSize.includes(s.Id))
    ),
    deepEquals
  );

  const nominalParticleSizeOptions = availableSieveSizes
    .map((s: ISieveSize) => ({ label: s.Caption, value: s.Id }))
    .concat([
      { value: AsNominalParticleSize.FineAggregate, label: 'Fine Aggregate' },
      { value: AsNominalParticleSize.Filler, label: 'Filler' },
    ]);

  return (
    <>
      <ListFieldSection>
        <Field
          input={<RadioGroup />}
          name="Preparation.MaterialDryMassObtainedBy"
          options={[
            { label: 'Measurement as part of this test', value: MaterialDryMassObtainedBy.Measured },
            { label: 'Imported from another test', value: MaterialDryMassObtainedBy.Imported },
          ]}
        />
      </ListFieldSection>

      {measuredDryMass && <AggsoilSpecimenPreparationPageSection />}
      {importedDryMass && (
        <>
          <WorksheetAssociatedTestSection
            name="WholePortion.Masses.AssociatedBinderOrBitumenContentTest"
            label="Preparation data"
            testType="Binder Content"
            defaultSelection={AssociatedTestType.AutoImported}
            associatedTestModel={{
              autoImported: true,
              imported: {
                importedTestResults: (
                  <ListFieldSection>
                    <Field input={<TextOutput />} name="WholePortion.Masses.DryMass" emph />
                    <Field input={<TextOutput />} name="Preparation.NominalParticleSize" emph />
                  </ListFieldSection>
                ),
                importModel: {
                  Id: importedBinderContentTestId,
                },
              },
              external: {
                testMethods: [
                  ExternalTestMethods.binderContent.AS_2891_3_2,
                  ExternalTestMethods.bitumenContent.AS_2891_3_1,
                  ExternalTestMethods.bitumenContent.AS_2891_3_3,
                ],
                externalTestResults: (
                  <ListFieldSection>
                    <Field
                      input={<NumericInput />}
                      name="WholePortion.Masses.AssociatedBinderOrBitumenContentTest.ExternalTest.Results.AggregateMass"
                    />
                    <Field
                      input={<Dropdown />}
                      name="WholePortion.Masses.AssociatedBinderOrBitumenContentTest.ExternalTest.Results.VisualNominalParticleSize"
                      options={nominalParticleSizeOptions}
                      simpleValue
                    />
                  </ListFieldSection>
                ),
              },
            }}
          />

          <ListFieldSection>
            <Field
              input={<TextOutput />}
              name="WholePortion.Preparation.StateBeforePreparation"
              value={getLabelForPortionState(StateOfMaterial.OvenDry)}
            />
          </ListFieldSection>
          <ListFieldSection>
            <Field
              input={<RadioGroup />}
              name="Preparation.AggregateType"
              options={[
                { label: 'Graded aggregate', value: AggregateType.GradedAggregate },
                { label: 'One-sized aggregate', value: AggregateType.OneSizedAggregate },
              ]}
            />
            <Field input={<Checkbox />} name="Preparation.MaterialContainsAsphaltFiller" disabled={true} />

            <Field input={<Checkbox />} name="Preparation.MaterialWashed" disabled={true} />
          </ListFieldSection>
        </>
      )}
    </>
  );
};

AsphaltSpecimenPreparationSection = withInjectedProps(AsphaltSpecimenPreparationSection);

export { AsphaltSpecimenPreparationSection };
export default AsphaltSpecimenPreparationSection;
